import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { USER_ATTRIBUTES } from 'collaboration-sidebar/constants/userAttributes';
import useUserSettings from 'collaboration-sidebar/hooks/useUserSettings';
import { MODULES_WITH_REPLIES, HIDE_EMBEDDED_COMMENTS } from 'collaboration-sidebar/constants/CollaborationSidebarStateProperties';
import { getAssetKeyFromAsset } from 'collaboration-sidebar/utils/multipleAssets';
import COMMENT_TYPES from '../../../constants/CommentTypes';
import useIsCommentsDataStale from '../../../hooks/useIsCommentsDataStale';
import useHasCommentingFeature from '../../../hooks/useHasCommentingFeature';
import { useRealtimeCommentHandler } from '../../../hooks/useHasUnreadComments';
import * as commentingApi from '../../../api/CommentingV3';
export default function useSetUpEmbeddedComments({
  collaborationSidebarState,
  onUpdateCollaborationSidebarState
}) {
  const {
    getUserSetting,
    hasLoaded: userSettingsLoaded
  } = useUserSettings();
  const hasCommentingFeature = useHasCommentingFeature();
  const assetKeyWithFetchedReplies = useRef(null);
  const [hasInitializedHideIndicators, setHasInitializedHideIndicators] = useState(false);
  const {
    isEmbeddedCommentingEnabled,
    objectId,
    objectType,
    isCommentModeActive,
    assetsLastUpdated,
    replyCounts
  } = collaborationSidebarState;
  const currentReplyCounts = useRef(replyCounts);
  useEffect(() => {
    currentReplyCounts.current = replyCounts;
  }, [replyCounts]);
  const isCommentsDataStale = useIsCommentsDataStale({
    objectType: objectType || undefined,
    objectId,
    assetsLastUpdated
  });
  const hideIndicators = useMemo(() => userSettingsLoaded ? getUserSetting(USER_ATTRIBUTES.HIDE_COLLABORATION_SIDEBAR_EMBEDDED_COMMENTS.key) === 'true' : true, [getUserSetting, userSettingsLoaded]);
  const fetchEmbeddedComments = useCallback(async stale => {
    const needsComments = stale || isCommentsDataStale;
    if (!hasCommentingFeature) {
      return;
    }
    const assetKey = getAssetKeyFromAsset({
      objectId: objectId || '',
      objectType: objectType || ''
    });
    if ((!hideIndicators || isCommentModeActive) && isEmbeddedCommentingEnabled && objectId && objectType && (assetKeyWithFetchedReplies.current !== assetKey || needsComments)) {
      var _response$results$fil, _response$results;
      const response = await commentingApi.fetchCommentsCached({
        objectType,
        objectId,
        refetch: needsComments
      });
      const replyComments = (_response$results$fil = (_response$results = response.results) === null || _response$results === void 0 ? void 0 : _response$results.filter(comment => comment.type === COMMENT_TYPES.DEEP_COMMENT && comment.parentId)) !== null && _response$results$fil !== void 0 ? _response$results$fil : [];
      const replyCommentsMap = replyComments.reduce((repliesAcc, comment) => {
        var _comment$parentId;
        repliesAcc[(_comment$parentId = comment.parentId) !== null && _comment$parentId !== void 0 ? _comment$parentId : ''] = comment;
        return repliesAcc;
      }, {});
      assetKeyWithFetchedReplies.current = assetKey;
      onUpdateCollaborationSidebarState({
        [MODULES_WITH_REPLIES]: replyCommentsMap
      });
      const latestReplyCounts = await commentingApi.fetchCommentRepliesCount({
        objectType,
        objectId,
        commentIds: replyComments.map(comment => comment.id)
      });
      onUpdateCollaborationSidebarState({
        replyCounts: Object.assign({}, currentReplyCounts.current, latestReplyCounts)
      });
    }
  }, [hasCommentingFeature, hideIndicators, isCommentModeActive, isCommentsDataStale, isEmbeddedCommentingEnabled, objectId, objectType, onUpdateCollaborationSidebarState]);
  useRealtimeCommentHandler({
    objectType: objectType || undefined,
    objectId: objectId || undefined,
    callback: () => void fetchEmbeddedComments(true)
  });

  // Fetch all comments to know which modules have comments to show the embedded comment indicators.
  // We don't fetch if the indicators are hidden to begin with, and only fetch when they're on, or if in comment mode.
  // TODO Delay this to not affect SLAs
  useEffect(() => {
    fetchEmbeddedComments().catch(__e => {
      // We shouldn't overshow errors here as user likely isn't
      // using commenting. Errors will show when opening sidebar
    });
  }, [fetchEmbeddedComments]);

  // Set initial value for hide indicators when we've fetched the user settings.
  useEffect(() => {
    if (!hasInitializedHideIndicators && userSettingsLoaded) {
      onUpdateCollaborationSidebarState({
        [HIDE_EMBEDDED_COMMENTS]: hideIndicators
      });
      setHasInitializedHideIndicators(true);
    }
  }, [hasInitializedHideIndicators, hideIndicators, userSettingsLoaded, onUpdateCollaborationSidebarState]);
}